import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import Image from 'next/future/image';
import { useEffect, useState } from 'react';
import { CreateCustomer, Customer } from '@lambda/apis/src/customer/types';
import {
  CustomerSubscriptionChannel,
  CustomerSubscriptionSource,
  CustomerSubscriptionStatus,
} from '@lambda/apis/src/customer/enum';
import { CtaButton } from '@/components/Cta/Cta';
import settings from '@/settings/configs';
import OpenEyeIcon from '@/public/icons/account/eye_open.svg';
import ClosedEyeIcon from '@/public/icons/account/eye_closed.svg';
import SocialLogin, {
  SocialLoginAction,
} from '@/components/accounts/SocialLogin';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import {
  trackSignup,
  trackSubscriptionPreferencesUpdated,
} from '@/lib/analytics/trackCustomer';
import i18n from '@/settings/i18n';
import { loadGoogleSigninScript } from '@/lib/google-signin';

const t = i18n.init();

const INPUT_FIELD =
  'my-1 appearance-none rounded-sm border border-solid border-gray-500 p-3 text-gray-700 outline-1 outline-gray-700';

const schema = yup
  .object()
  .shape({
    // only last name is required as full name input for Korea
    firstName: yup.lazy(() =>
      yup.string().required(t`First name is a required field`),
    ),
    lastName: yup.string().required(t`Last name is a required field`),
    email: yup
      .string()
      .email(t`Email must be a valid email`)
      .required(t`Email is a required field`),
    password: yup
      .string()
      .min(6, t`Password must be at least 6 characters`)
      .required(t`Password is a required field`),
    acceptsMarketing: yup.bool(),
  })
  .required();

type Form = yup.Asserts<typeof schema>;

export default function RegisterForm(props: {
  header?: JSX.Element;
  onSuccess: (customer: Customer) => void;
}) {
  const form = useForm<Form>({ resolver: yupResolver(schema) });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { signup, login, loading, error, updateEmailSubscription } =
    useCustomerContext();

  useEffect(() => {
    loadGoogleSigninScript();
  }, []);

  const signupAndCallback = async (input: {
    acceptsMarketing: boolean;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    const req: CreateCustomer = {
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      password: input.password,
    };

    let customer = await signup(req);

    if (error) {
      form.setError('password', {
        type: 'manual',
        message: error,
      });
    }

    if (!customer) return;

    customer = await login(input.email, input.password);

    if (!customer) return;

    const emailSubscriptionStatus = input.acceptsMarketing
      ? CustomerSubscriptionStatus.SUBSCRIBED
      : CustomerSubscriptionStatus.UNSUBSCRIBED;

    trackSignup();

    await updateEmailSubscription({
      customerId: customer.reebeloId,
      status: emailSubscriptionStatus,
      source: CustomerSubscriptionSource.SIGNUP,
    });

    trackSubscriptionPreferencesUpdated(
      input.email,
      emailSubscriptionStatus,
      CustomerSubscriptionChannel.EMAIL,
      CustomerSubscriptionSource.SIGNUP,
    );

    props.onSuccess(customer);
  };

  return (
    <>
      {props.header}
      <div className="flex w-full flex-col">
        <input
          className={INPUT_FIELD}
          placeholder={t`First name`}
          type="text"
          {...form.register('firstName')}
        />
        {form.formState.errors.firstName && (
          <div className="text-xs text-red">
            {form.formState.errors.firstName.message}
          </div>
        )}
      </div>
      <div className="flex w-full flex-col">
        <input
          className={INPUT_FIELD}
          placeholder={t`Last name`}
          {...form.register('lastName')}
        />
        {form.formState.errors.lastName && (
          <div className="text-xs text-red">
            {form.formState.errors.lastName.message}
          </div>
        )}
      </div>
      <div className="flex w-full flex-col">
        <input
          className={INPUT_FIELD}
          placeholder={t`Email`}
          type="email"
          {...form.register('email')}
        />
        {form.formState.errors.email && (
          <div className="text-xs text-red">
            {form.formState.errors.email.message}
          </div>
        )}
      </div>
      <div className="relative flex w-full flex-col">
        <input
          className={INPUT_FIELD}
          placeholder={t`Password`}
          type={passwordVisible ? 'text' : 'password'}
          {...form.register('password')}
        />
        <button
          className={
            'absolute right-2 mt-5 flex h-[20px] w-[20px] translate-x-0.5 cursor-pointer items-center justify-center bg-none'
          }
          onClick={() => {
            setPasswordVisible(!passwordVisible);
          }}
          type="button"
        >
          <span className="h-[20px] w-[20px]">
            <Image
              src={passwordVisible ? OpenEyeIcon : ClosedEyeIcon}
              width={20}
              height={20}
              alt="Add to Wishlist"
              className="h-full w-[20px] object-contain"
              priority
            />
          </span>
        </button>
        {form.formState.errors.password && (
          <div className="text-xs text-red">
            {form.formState.errors.password.message}
          </div>
        )}
      </div>
      <div className="my-2 text-left text-xxs text-gray-400 md:text-xs">
        <input
          {...form.register('acceptsMarketing')}
          className="accent-gray-700"
          type="checkbox"
          id="acceptsMarketing"
          name="acceptsMarketing"
          defaultChecked={settings.store !== 'reebelo-ca'}
        />
        <label htmlFor="acceptsMarketing" className="ml-2 align-top">
          Subscribe to our newsletter for exclusive content and early access to
          promotions.
        </label>
      </div>
      <div className="mt-1 mb-3 text-left text-xxs text-gray-400 md:text-xs">
        By creating an account, you agree to Reebelo's
        <Link href={'/policies/terms-of-service'}>
          <a className="cursor-pointer text-black">{` Terms of Service `}</a>
        </Link>
        and
        <Link href={'/policies/privacy-policy'}>
          <a className="cursor-pointer text-black">{` Privacy Policy.`}</a>
        </Link>
      </div>
      <CtaButton
        type="submit"
        className="my-2 w-full !px-3 !py-2"
        onClick={form.handleSubmit(signupAndCallback)}
        loading={loading}
      >
        {t`Create my account`}
      </CtaButton>
      <SocialLogin
        action={SocialLoginAction.SIGNUP}
        onSuccess={props.onSuccess}
      />
    </>
  );
}
